import type { User } from "oidc-client-ts";
import { embedToken, renewEmbedToken } from "../embed/session";
import { userManager, type Realm } from "./realms";

/**
 * How long a renewal that Keycloak refused waits for the tokens of another renewal: the library's own renewal on its
 * expiry timer may have used the same refresh token a moment earlier (Keycloak rotates it), and then its tokens are on
 * their way.
 */
const RACE_WAIT_MS = 3_000;

const renewals = new Map<Realm, Promise<string | undefined>>();

function nextLoadedUser(realm: Realm, ms: number): Promise<User | undefined> {
  const events = userManager(realm).events;
  return new Promise((resolve) => {
    const done = (u: User | undefined) => {
      clearTimeout(timer);
      events.removeUserLoaded(onLoaded);
      resolve(u);
    };
    const onLoaded = (u: User) => done(u);
    const timer = setTimeout(() => done(undefined), ms);
    events.addUserLoaded(onLoaded);
  });
}

async function renew(realm: Realm): Promise<string | undefined> {
  const manager = userManager(realm);
  const before = await manager.getUser();
  if (!before?.refresh_token) return undefined;
  try {
    const u = await manager.signinSilent();
    return u && !u.expired ? u.access_token : undefined;
  } catch {
    // Lost a race with the other renewal, or the session ended. AuthProvider decides about the session through the
    // library's own renewal (onRenewError); here a call only goes without a token.
    const now = await manager.getUser();
    if (now && !now.expired && now.access_token !== before.access_token) return now.access_token;
    const loaded = await nextLoadedUser(realm, RACE_WAIT_MS);
    return loaded && !loaded.expired ? loaded.access_token : undefined;
  }
}

/**
 * Renews the realm's tokens with the refresh token now, once for every caller that asks while a renewal is in flight
 * (a laptop waking from sleep refetches every screen at once). Resolves to the new access token, or undefined when
 * there is no refresh token or the renewal failed.
 */
export function renewAccessToken(realm: Realm): Promise<string | undefined> {
  // Inside a phone app's web-view widget the app renews its own token (mobile-dashboards-2 MD2-03).
  if (realm === "customers" && embedToken()) return renewEmbedToken();
  let p = renewals.get(realm);
  if (!p) {
    p = renew(realm)
      .catch(() => undefined)
      .finally(() => renewals.delete(realm));
    renewals.set(realm, p);
  }
  return p;
}

/**
 * The realm's access token for a call or a socket: the current one, or, when it has expired and the renewal on the
 * library's timer has not happened yet (woken from sleep), a renewed one. Undefined when signed out or when the
 * renewal failed.
 */
export async function accessToken(realm: Realm): Promise<string | undefined> {
  const embedded = realm === "customers" ? embedToken() : undefined;
  if (embedded) return embedded;
  const u = await userManager(realm).getUser();
  if (!u) return undefined;
  if (!u.expired) return u.access_token;
  return renewAccessToken(realm);
}

export function resetRenewalsForTests(): void {
  renewals.clear();
}
